import { DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { GeocodingJobStatus, GeocodingTask, GeocodingTaskType } from '~src/geocoding/requests/GeocodingBackgrounderRequests';
import { GeocodingConfig } from '~src/geocoding/requests/GeocodingConfigRequests';

import { GeocodingRecordActionCellFormatter } from '../formatters/GeocodingRecordActionCellFormatter';
import { GeocodingRecordActionHeaderFormatter } from '../formatters/GeocodingRecordActionHeaderFormatter';
import { GeocodingRecordHeaderFormatter } from '../formatters/GeocodingRecordHeaderFormatter';
import { GeocodingRecordNameCellFormatter } from '../formatters/GeocodingRecordNameCellFormatter';

const ROW_HEIGHT = 30;

const DEFAULT_SCHEDULE_INFO = '-';

export const GeocodingRecordDGConstants = {
  ConfigId: 'GeocodingRecordDG_ConfigId',
  Name: 'GeocodingRecordDG_Name',
  Run: 'GeocodingRecordDG_Run',
  Frequency: 'GeocodingRecordDG_Frequency',
  Schedule: 'GeocodingRecordDG_Schedule',
  Status: 'GeocodingRecordDG_Status',
  Action: 'GeocodingRecordDG_Action',
  GeocodingConfigObj: 'GeocodingRecordDG_GeocodingConfigObj',
  HandleEdit: 'GeocodingRecordDG_HandleEdit',
  HandleDelete: 'GeocodingRecordDG_HandleDelete',
};

export type GeocodingRecordItem = {
  config: GeocodingConfig;
  task?: GeocodingTask;
}

export interface GeocodingRecordDataGridProps {
  configRecords: GeocodingRecordItem[];
  handleEditConfig: (geocodingConfig: GeocodingRecordItem) => void;
  handleDeleteConfig: (configId: string) => void;
}

export class GeocodingRecordDataGrid extends React.Component<GeocodingRecordDataGridProps, {}> {
  public getGeocodingConfigRows(): DataGrid.Row[] {
    const rows: DataGrid.Row[] = [];
    this.props.configRecords.forEach((record: GeocodingRecordItem) => {
      let row: DataGrid.Row = {
        key: 'r-' + record.config.id,
        data: {},
      };
      row.data[GeocodingRecordDGConstants.ConfigId] = record.config.id;
      row.data[GeocodingRecordDGConstants.GeocodingConfigObj] = record;
      row.data[GeocodingRecordDGConstants.Run] = this.getRunFriendlyName(record.task?.type);
      row.data[GeocodingRecordDGConstants.Frequency] = record.task?.schedule.frequency ?? DEFAULT_SCHEDULE_INFO;
      row.data[GeocodingRecordDGConstants.Schedule] = record.task?.schedule.nextRunAt ? L10n.localizedDateTime(record.task?.schedule.nextRunAt) : DEFAULT_SCHEDULE_INFO;
      row.data[GeocodingRecordDGConstants.Status] = record.task?.lastRunJob?.status ? this.getLatestJobStatus(record.task.lastRunJob.status) : DEFAULT_SCHEDULE_INFO;
      row.data[GeocodingRecordDGConstants.HandleEdit] = this.handleEditClick;
      row.data[GeocodingRecordDGConstants.HandleDelete] = this.handleDeleteClick;
      rows.push(row);
    });
    return rows;
  }

  private getLatestJobStatus(status: GeocodingJobStatus) {
    switch (status) {
      case GeocodingJobStatus.Pending:
        return Messages.geocodingRecordStatusInProgress();
      case GeocodingJobStatus.InProgress:
        return Messages.geocodingRecordStatusInProgress();
      case GeocodingJobStatus.Success:
        return Messages.geocodingRecordStatusSuccess();
      case GeocodingJobStatus.Cancelled:
        return Messages.geocodingRecordStatusCancelled();
      default:
        return DEFAULT_SCHEDULE_INFO;
    }
  }

  private getRunFriendlyName(taskType: GeocodingTaskType)
  {
    // TODO: RunType likely to be removed from in the new initialization dialog designs
    // Move these strings to Messages if they still end up being needed
    switch (taskType) {
      case GeocodingTaskType.EstimateAddressGeocodingTask:
        return 'Estimate';
      case GeocodingTaskType.RunAddressGeocodingTask:
      default:
        return 'Geocoding';
    }
  }


  private buildColumn(key: string, name: string, cellFormatter?: DataGrid.CellComponent, headerFormatter?: DataGrid.HeaderComponent): DataGrid.Column {
    return {
      name: name,
      dataKey: key,
      headerText: name,
      growToFit: true,
      resizable: false,
      sortable: key !== GeocodingRecordDGConstants.Action,
      cellComponent: cellFormatter,
      headerComponent: headerFormatter ?? GeocodingRecordHeaderFormatter
    };
  }

  private getColumns(): DataGrid.Column[] {
    const cols: DataGrid.Column[] = [
      this.buildColumn(GeocodingRecordDGConstants.Name, Messages.geocodingRecordHeaderName(), GeocodingRecordNameCellFormatter),
      this.buildColumn(GeocodingRecordDGConstants.Run, Messages.geocodingRecordHeaderType()),
      this.buildColumn(GeocodingRecordDGConstants.Frequency, Messages.geocodingRecordHeaderFrequency()),
      this.buildColumn(GeocodingRecordDGConstants.Schedule, Messages.geocodingRecordHeaderNextRun()),
      this.buildColumn(GeocodingRecordDGConstants.Status, Messages.geocodingRecordHeaderStatus()),
      this.buildColumn(GeocodingRecordDGConstants.Action, Messages.geocodingRecordHeaderAction(), GeocodingRecordActionCellFormatter, GeocodingRecordActionHeaderFormatter),
    ];
    return cols;
  }

  private getRowStore(): DataGrid.RowStore {
    const allRows = this.getGeocodingConfigRows();
    return {
      getItems: (_: number, __: number, rowHandler: (rows: Promise<DataGrid.Row[]> | DataGrid.Row[]) => void) => {
        rowHandler(allRows);
      },
    };
  }

  private handleEditClick = (configRecord: GeocodingRecordItem): void => {
    this.props.handleEditConfig(configRecord);
  };

  private handleDeleteClick = (configId: string): void => this.props.handleDeleteConfig(configId);

  public render(): JSX.Element {
    if (this.props.configRecords.length === 0) {
      return;
    }

    const dataGridProps: DataGrid.Props = {
      cols: this.getColumns(),
      rowStore: this.getRowStore(),
      showRowBanding: false,
      headerHeight: ROW_HEIGHT,
      config: { rowOuterHeight: ROW_HEIGHT },
    };

    return (
      <div className='geocodingConfigRecordsDatagrid'>
        <DataGrid {...dataGridProps} />
      </div>
    );
  }
}
